import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { fetchMeta, fetchDashboard } from './api';
import { EMPTY_FILTERS } from './constants';

/**
 * Состояние экрана «Школы» (client state из постановки, п.3): язык — глобальный i18next,
 * а три фильтра-разреза + данные экрана живут здесь. Один источник правды: KPI, диаграммы
 * и таблица всегда показывают одну и ту же выборку; те же фильтры уходят в экспорт.
 *
 * Логика загрузки:
 *   • meta — один раз при монтировании;
 *   • dashboard — при загрузке и при каждом изменении фильтров (повторный запрос);
 *   • смена языка запроса НЕ вызывает (подписи уже пришли в двух языках).
 */

const SchoolsContext = createContext(null);

const initialState = {
  meta: null,
  data: null, // { summary, cuts, items }
  filters: EMPTY_FILTERS,
  loading: true,
  error: false,
  reloadToken: 0,
};

function reducer(state, action) {
  switch (action.type) {
    case 'META':
      return { ...state, meta: action.meta };
    case 'LOAD':
      return { ...state, loading: true, error: false };
    case 'DATA':
      return { ...state, data: action.data, loading: false };
    case 'ERROR':
      return { ...state, error: true, loading: false };
    case 'SET_FILTER':
      return { ...state, filters: { ...state.filters, [action.field]: action.value || '' } };
    case 'TOGGLE':
      // клик по сектору/строке легенды = тот же фильтр; повтор снимает
      return {
        ...state,
        filters: { ...state.filters, [action.field]: state.filters[action.field] === action.key ? '' : action.key },
      };
    case 'RESET':
      return { ...state, filters: EMPTY_FILTERS };
    case 'RELOAD':
      return { ...state, reloadToken: state.reloadToken + 1 };
    default:
      return state;
  }
}

export function SchoolsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // meta — один раз
  useEffect(() => {
    let alive = true;
    fetchMeta()
      .then((m) => alive && dispatch({ type: 'META', meta: m }))
      .catch(() => alive && dispatch({ type: 'ERROR' }));
    return () => {
      alive = false;
    };
  }, []);

  // dashboard — при загрузке, при смене фильтров и по кнопке «Повторить»
  useEffect(() => {
    let alive = true;
    dispatch({ type: 'LOAD' });
    fetchDashboard(state.filters)
      .then((d) => alive && dispatch({ type: 'DATA', data: d }))
      .catch(() => alive && dispatch({ type: 'ERROR' }));
    return () => {
      alive = false;
    };
  }, [state.filters, state.reloadToken]);

  const setFilter = useCallback((field, value) => dispatch({ type: 'SET_FILTER', field, value }), []);
  const toggleFilter = useCallback((field, key) => dispatch({ type: 'TOGGLE', field, key }), []);
  const resetFilters = useCallback(() => dispatch({ type: 'RESET' }), []);
  const reload = useCallback(() => dispatch({ type: 'RELOAD' }), []);

  const value = {
    meta: state.meta,
    data: state.data,
    filters: state.filters,
    loading: state.loading,
    error: state.error,
    setFilter,
    toggleFilter,
    resetFilters,
    reload,
  };

  return <SchoolsContext.Provider value={value}>{children}</SchoolsContext.Provider>;
}

export function useSchools() {
  const ctx = useContext(SchoolsContext);
  if (!ctx) throw new Error('useSchools must be used within <SchoolsProvider>');
  return ctx;
}
